<template>
  <aside
    v-if="user.isLoggedIn"
    class="bui-nav"
    :class="{ collapsed, overflow }"
    :data-state="state"
    :aria-label="'Primary navigation'"
  >
    <div class="rail-inner">
      <NavToggle :pressed="collapsed" @click="toggle" />

      <NavLogo :collapsed="collapsed" />

      <!-- Top list -->
      <ul class="list list-1">
        <li v-for="item in top" :key="item.label">
          <NavItem
            :to="item.path"
            :label="item.label"
            :exact="item.exact"
            :narrow="collapsed"
          >
            <template #icon>
              <component :is="item.icon" />
            </template>
          </NavItem>
        </li>
      </ul>

      <!-- Bottom block -->
      <div class="bottom">
        <!-- Standard layout (no overflow): items are visible inline -->
        <ul v-if="!overflow" class="list list-2">
          <BottomItems
            :collapsed="collapsed"
            :bottom="bottom"
            :user="user"
            @logout="logout"
          />
        </ul>

        <!-- Overflow layout: bottom items collapse under "More" -->
        <template v-else>
          <NavItemOverflow
            v-model:open="moreOpen"
            label="More"
            :narrow="collapsed"
          >
            <ul class="list list-2 nested">
              <BottomItems
                :collapsed="collapsed"
                :bottom="bottom"
                :user="user"
                :exclude-status="true"
                @logout="logout"
              />
            </ul>
          </NavItemOverflow>

          <!-- System Status remains visible outside the overflow panel. -->
          <NavItem
            to="/uptime"
            label="System Status"
            :narrow="collapsed"
            class="system-status"
          >
            <template #icon>
              <NavStatusIco status="good" />
            </template>
          </NavItem>
        </template>
      </div>
    </div>
  </aside>
</template>

<script lang="ts" setup>
import { computed, h, defineComponent, type PropType } from 'vue';

import useUser from '@/composables/user';
import NavToggle from '@/components/bui/atoms/NavToggle/NavToggle.vue';
import NavLogo from '@/components/bui/atoms/NavLogo/NavLogo.vue';
import NavStatusIco from '@/components/bui/atoms/NavStatusIco/NavStatusIco.vue';
import { PhUser, PhSignOut } from '@phosphor-icons/vue';
import NavItem from '@/components/bui/molecules/NavItem/NavItem.vue';
import NavItemExtra from '@/components/bui/molecules/NavItemExtra/NavItemExtra.vue';
import NavItemDropdown from '@/components/bui/molecules/NavItemDropdown/NavItemDropdown.vue';
import NavItemOverflow from '@/components/bui/molecules/NavItemOverflow/NavItemOverflow.vue';

import { useNavCollapse } from './useNavCollapse';
import { useNavOverflow } from './useNavOverflow';
import {
  topNavItems,
  bottomNavItems,
  filterItems,
  type NavItemConfig,
} from './navItems.config';

const user = useUser();
const { collapsed, toggle } = useNavCollapse();
const { overflow } = useNavOverflow();

const moreOpen = computed({
  get: () => moreOpenInternal.value,
  set: (v: boolean) => (moreOpenInternal.value = v),
});

import { ref } from 'vue';
const moreOpenInternal = ref(false);

const top = computed(() => filterItems(topNavItems, user));
const bottom = computed(() => filterItems(bottomNavItems, user));

const state = computed(() => {
  if (overflow.value) {
    if (collapsed.value) return moreOpen.value ? 'close-overflow-open' : 'closed-overflow';
    return moreOpen.value ? 'open-overflow-open' : 'open-overflow';
  }
  return collapsed.value ? 'closed' : 'open';
});

function logout() {
  user.logout();
}

// Local helper component to avoid repeating the bottom-item rendering markup
// for both the normal layout and the overflow popover.
const BottomItems = defineComponent({
  name: 'BuiBottomItems',
  props: {
    bottom: { type: Array as PropType<NavItemConfig[]>, required: true },
    collapsed: { type: Boolean, default: false },
    user: { type: Object, required: true },
    excludeStatus: { type: Boolean, default: false },
  },
  emits: ['logout'],
  setup(props, { emit }) {
    return () => {
      const userPath = `/users/edit/${props.user.userId}`;
      const items: any[] = [];

      // 1. User row (NavItemExtra) — uses fullName from user store
      items.push(
        h(
          'li',
          { key: 'user' },
          h(NavItemExtra, {
            to: userPath,
            label: props.user.fullName || 'Account',
            narrow: props.collapsed,
            actionLabel: 'Log out',
            onAction: () => emit('logout'),
          }, {
            icon: () => h(PhUser),
            'action-icon': () => h(PhSignOut),
          }),
        ),
      );

      // 2. Settings (dropdown) + Help + others
      for (const item of props.bottom) {
        if (item.subItems) {
          items.push(
            h(
              'li',
              { key: item.label },
              h(
                NavItemDropdown,
                {
                  label: item.label,
                  narrow: props.collapsed,
                  childPaths: item.subItems
                    .map((s) => s.path)
                    .filter((p): p is string => !!p),
                },
                {
                  icon: () => h(item.icon),
                  default: () =>
                    item.subItems!.map((sub) =>
                      h(
                        NavItem,
                        {
                          to: sub.path,
                          label: sub.label,
                          /* Flyout uses full labels (rail may be collapsed). */
                          narrow: false,
                        },
                        sub.icon
                          ? { icon: () => h(sub.icon!) }
                          : {},
                      ),
                    ),
                },
              ),
            ),
          );
        } else {
          items.push(
            h(
              'li',
              { key: item.label },
              h(NavItem, {
                to: item.path,
                label: item.label,
                narrow: props.collapsed,
              }, {
                icon: () => h(item.icon),
              }),
            ),
          );
        }
      }

      // 3. System Status — only when not excluded (in overflow mode it sits
      //    outside the popover, so the popover passes excludeStatus=true).
      if (!props.excludeStatus) {
        items.push(
          h(
            'li',
            { key: 'system-status' },
            h(
              NavItem,
              {
                to: '/uptime',
                label: 'System Status',
                narrow: props.collapsed,
              },
              {
                icon: () => h(NavStatusIco, { status: 'good' }),
              },
            ),
          ),
        );
      }

      return items;
    };
  },
});
</script>

<style lang="scss" scoped>
.bui-nav {
  background: var(--nav-fill, #6b1ab1);
  color: var(--nav-label-primary, #f6f7ff);
  width: var(--nav-width-open, 320px);
  /* Cap rail to viewport so bottom (System Status) stays reachable without page scroll. */
  height: 100vh;
  max-height: 100vh;
  min-height: 0;
  position: sticky;
  top: 0;
  align-self: flex-start;
  box-shadow: var(--nav-shadow-inset, inset -4px 0 8px 0 rgba(25, 22, 64, 0.32));
  flex-shrink: 0;
  font-family: var(--nav-font-family, 'Robout', system-ui, sans-serif);
  font-weight: var(--nav-font-weight, 300);
  z-index: 50;
  transition: width 200ms ease-out;

  @media (max-width: 768px) {
    --nav-width-open: 224px;
    --nav-width-closed: 56px;
    --nav-item-font-size: 14px;
    --nav-item-line-height: 17px;
    --nav-item-narrow-w: 44px;
    --nav-padding-box: 5px;
    --nav-padding-icon: 5px;
    --nav-radius-box: 9px;
  }

  &.collapsed {
    width: var(--nav-width-closed, 81px);

    /* Tighter right inset: scrollbar sits nearer the outer rail edge without widening the aside. */
    .rail-inner {
      padding: 32px 5px 56px 12px;
    }

    .list-1 {
      /* Rail is narrow — don’t reserve extra inline space for the scrollbar. */
      padding-inline-end: 0;
      scrollbar-width: thin;

      &::-webkit-scrollbar {
        width: 4px;
      }

      &::-webkit-scrollbar-thumb {
        background-color: color-mix(in srgb, var(--nav-label-primary, #f6f7ff) 38%, transparent);
        background-clip: padding-box;
        border: 0 solid transparent;
        border-left: 2px solid transparent;
        border-radius: 999px;
      }

      &::-webkit-scrollbar-thumb:hover {
        background-color: color-mix(in srgb, var(--nav-label-primary, #f6f7ff) 55%, transparent);
        background-clip: padding-box;
        border: 0 solid transparent;
        border-left: 2px solid transparent;
        border-radius: 999px;
      }
    }
  }
}

.rail-inner {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px 12px 56px;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  box-sizing: border-box;
  /* Popovers/panels use position:absolute; keep visible so they aren’t clipped here. */
  overflow: visible;
  /* Expanded: don’t stretch the hamburger to full rail width (hover hit-area stays icon-sized).
     Stay flex-start when collapsing too — `center` would re-center in the still-wide rail mid-width animation. */
  align-items: flex-start;

  /* Toggle + logo don’t steal scroll space from the main nav list. */
  > :not(.list-1):not(.bottom) {
    flex-shrink: 0;
  }
}

.list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.list-1 {
  flex: 1 1 auto;
  min-height: 0;
  /* `overflow-x: visible` + `overflow-y: auto` is treated as both auto — causes a useless X scrollbar. */
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  /* Keep row hovers from running under the scrollbar; Firefox has no thumb offset API. */
  padding-inline-end: 6px;
  /* Desktop defaults to a wide OS scrollbar; mobile uses a thin overlay. Align desktop with that look. */
  scrollbar-width: thin;
  scrollbar-color: color-mix(in srgb, var(--nav-label-primary, #f6f7ff) 38%, transparent)
    transparent;

  &::-webkit-scrollbar {
    /* Gutter width; transparent border on thumb keeps the visible thumb thin and to the inline-end. */
    width: 9px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: color-mix(in srgb, var(--nav-label-primary, #f6f7ff) 38%, transparent);
    border-radius: 999px;
    background-clip: padding-box;
    border: 1px solid transparent;
    border-left: 5px solid transparent;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: color-mix(in srgb, var(--nav-label-primary, #f6f7ff) 55%, transparent);
    border: 1px solid transparent;
    border-left: 5px solid transparent;
    border-radius: 999px;
  }
}

.bottom {
  flex-shrink: 0;
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 0;
  position: relative;
  z-index: 0;
}

.list-2 {
  &.nested {
    gap: 6px;
    width: 100%;
    min-width: 0;
  }
}

@media (max-width: 768px) {
  .rail-inner {
    gap: 10px;
    padding: 12px 8px 40px;
  }

  .bui-nav.collapsed .rail-inner {
    padding: 12px 4px 40px 6px;
  }

  .list {
    gap: 3px;
  }

  .bottom {
    gap: 6px;
  }
}

.system-status {
  align-self: stretch;
}
</style>
